import { useState } from 'react';
import { Box, Card, CardContent, CardActions, Button, Typography, Stack, TextField, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DownloadIcon from '@mui/icons-material/Download';
import { api } from '../api/client';
import { useAuthStore } from '../auth/store';

interface ReportDef {
  id: string;
  title: string;
  endpoint: string;
  exportEndpoint?: string;
  needsRange: boolean;
  needsDate?: boolean;
}

const reports: ReportDef[] = [
  { id: 'R01', title: 'Visitas por día', endpoint: '/reports/daily-visits', exportEndpoint: '/reports/daily-visits/export', needsRange: true },
  { id: 'R04', title: 'Ausencias del día', endpoint: '/reports/absences', exportEndpoint: '/reports/absences/export', needsRange: false, needsDate: true },
  { id: 'R05', title: 'Tiempo dentro (por camioneta)', endpoint: '/reports/time-inside', needsRange: true },
  { id: 'R07', title: 'Eventos no resueltos', endpoint: '/reports/unresolved', needsRange: true },
  { id: 'R08', title: 'Calidad de la IA', endpoint: '/reports/ai-quality', needsRange: true },
  { id: 'R09', title: 'Top vehículos', endpoint: '/reports/top-vehicles', needsRange: true },
];

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function isoDaysAgo(d: number): string { const dt = new Date(); dt.setDate(dt.getDate() - d); return dt.toISOString().slice(0, 10); }

export function ReportsPage() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const token = useAuthStore.getState().accessToken;

  const fetchReport = async (def: ReportDef) => {
    setBusy(def.id);
    try {
      const params = def.needsDate ? { date } : { from, to };
      const res = await api.get(def.endpoint, { params });
      setData({ id: def.id, title: def.title, payload: res.data });
    } finally {
      setBusy(null);
    }
  };

  const exportXlsx = async (def: ReportDef) => {
    if (!def.exportEndpoint) return;
    const params = new URLSearchParams(def.needsDate ? { date } : { from, to });
    const url = `/api/v1${def.exportEndpoint}?${params.toString()}`;
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) { alert('Error al exportar'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${def.id}_${(def.needsDate ? date : `${from}_${to}`)}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Reportes</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filtros</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="Desde" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Hasta" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Fecha (R04)" type="date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Stack>
      </Paper>
      <Grid container spacing={2}>
        {reports.map((r) => (
          <Grid key={r.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="caption">{r.id}</Typography>
                <Typography variant="h6">{r.title}</Typography>
              </CardContent>
              <CardActions>
                <Button onClick={() => fetchReport(r)} disabled={busy === r.id}>Ver</Button>
                {r.exportEndpoint && (
                  <Button startIcon={<DownloadIcon />} onClick={() => exportXlsx(r)}>Excel</Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      {data ? (
        <Paper sx={{ p: 2, mt: 3, maxHeight: 480, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{(data as { title: string }).title}</Typography>
          <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify((data as { payload: unknown }).payload, null, 2)}</pre>
        </Paper>
      ) : null}
    </Box>
  );
}
